// The startup screen's audio download (Axel 2026-10-07): on the first start, and whenever new audio
// exists (a new build with new or changed clips, the partner's new takes), every recording is
// downloaded onto the device before the app opens, with a progress bar, so everything plays at once
// and offline. What is fetched: shell/audio-preload-plan.ts; how: shell/audio-preload.ts.
//
// Never a trap: offline it never blocks; a few small files (the partner's new takes) are fetched
// without the screen unless they take a moment; a lost connection, repeated failures or a full
// storage show "Yritä uudelleen" and "Jatka ilman ääniä", and the next start tries again.
// Asetukset → Ääni → "Äänet laitteelle" (listen/OfflineAudio.tsx) still shows the storage used and
// downloads again on demand, with the same plan and download (shell/audio-preload-deps.ts).
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useT } from '../i18n';
import { useApp } from '../state/store';
import type { PreloadProgress } from './audio-preload';
import { audioPreloadDeps, type AudioPreloadDeps } from './audio-preload-deps';
import { preloadStart, QUIET_SHOW_AFTER_MS, STORAGE_MARGIN } from './audio-preload-plan';
import { onUpdateGateChange, startupBusy, warmupOver } from './startup-gates';
import './audio-gate.css';
import { setAudioGateUp } from './tour/signals';

export type AudioGateDeps = AudioPreloadDeps;
export const defaultAudioGateDeps: AudioGateDeps = audioPreloadDeps;

type View =
  | { s: 'hidden' }
  /** p: null while the files are being looked up again (Yritä uudelleen) */
  | { s: 'loading'; p: PreloadProgress | null }
  | { s: 'stopped'; reason: 'network' | 'offline' | 'storage'; missing: number; need?: number; free?: number };

export function AudioGate({ deps = defaultAudioGateDeps }: { deps?: AudioGateDeps }) {
  const t = useT();
  const A = t.audioPreload;
  const phase = useApp((s) => s.phase);
  const identity = useApp((s) => s.identity);
  const needsLogin = useApp((s) => s.needsLogin);
  const signedIn = (identity === 'server' || identity === 'cached') && !needsLogin;
  // the update check's screen and the warm-up (shell/warmup/) go first; the download starts after the
  // warm-up, so its files do not share the connection with the sections' code
  const updateBusy = useSyncExternalStore(onUpdateGateChange, startupBusy, () => false);
  const warmed = useSyncExternalStore(onUpdateGateChange, warmupOver, () => true);
  const [view, setView] = useState<View>({ s: 'hidden' });
  const [attempt, setAttempt] = useState(0);
  const [enabled] = useState(() => deps.enabled());
  const retry = () => {
    setView({ s: 'loading', p: null });
    setAttempt((n) => n + 1);
  };

  // once per page when the app is ready and someone is signed in (again after a new sign-in, and
  // for each "Yritä uudelleen")
  useEffect(() => {
    if (!enabled || phase !== 'ready' || !signedIn || !warmed) return;
    const ctl = new AbortController();
    let live = true;
    let shown = false;
    let last: PreloadProgress | null = null;
    let showTimer: ReturnType<typeof setTimeout> | null = null;
    const show = () => {
      shown = true;
      if (live && last) setView({ s: 'loading', p: last });
    };
    void (async () => {
      const g = await deps.gather().catch(() => null);
      if (!live) return;
      if (!g) return setView({ s: 'hidden' });
      // the session is gone: the login screen comes, and after it this runs again
      if (g.auth) return setView({ s: 'hidden' });
      const start = preloadStart(g.plan, deps.online());
      if (start === 'open') return setView({ s: 'hidden' });
      const room = await deps.room(g.plan.todoBytes);
      if (!live) return;
      if (!room.fits) {
        if (start === 'block')
          setView({
            s: 'stopped',
            reason: 'storage',
            missing: g.plan.todo.length,
            need: g.plan.todoBytes + STORAGE_MARGIN,
            ...(room.free !== undefined ? { free: room.free } : {}),
          });
        return;
      }
      if (start === 'block' || attempt > 0) show();
      else showTimer = setTimeout(show, QUIET_SHOW_AFTER_MS);
      const result = await deps.run(
        g.plan.todo,
        (p) => {
          last = p;
          if (shown && live) setView({ s: 'loading', p });
        },
        ctl.signal,
      );
      if (showTimer) clearTimeout(showTimer);
      if (!live) return;
      if (result.status === 'stopped' && shown) setView({ s: 'stopped', reason: result.reason, missing: result.missing });
      else setView({ s: 'hidden' });
    })();
    return () => {
      live = false;
      if (showTimer) clearTimeout(showTimer);
      ctl.abort();
    };
  }, [deps, enabled, phase, signedIn, warmed, attempt]);

  // the connection came back while the screen says it was lost: carry on by itself
  const stoppedOffline = view.s === 'stopped' && view.reason === 'offline';
  useEffect(() => {
    if (!stoppedOffline) return;
    const on = () => {
      setView({ s: 'loading', p: null });
      setAttempt((n) => n + 1);
    };
    window.addEventListener('online', on);
    return () => window.removeEventListener('online', on);
  }, [stoppedOffline]);

  // the app's tours wait while this screen is up (shell/tour/signals.ts)
  const up = view.s !== 'hidden' && !updateBusy;
  useEffect(() => setAudioGateUp(up), [up]);
  useEffect(() => () => setAudioGateUp(false), []);

  // the update check's screen goes first (a new version reloads the page)
  if (view.s === 'hidden' || updateBusy) return null;
  if (view.s === 'loading') {
    const { p } = view;
    const pct = p && p.bytesTotal > 0 ? Math.min(100, Math.round((p.bytes / p.bytesTotal) * 100)) : 0;
    return (
      <div className="update-gate audio-gate">
        <div className="update-gate-inner">
          <p className="update-gate-name">Valyrio udrir</p>
          <div
            className="update-gate-line audio-gate-bar"
            role="progressbar"
            aria-label={A.loading}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            {...(p ? { 'aria-valuetext': A.amountLabel(p.bytes, p.bytesTotal) } : {})}
          >
            <span style={{ width: `${pct}%` }} />
          </div>
          <p className="update-gate-status" role="status">
            {A.loading}
          </p>
          {/* the line keeps its place while the files are looked up again */}
          <p className="update-gate-status audio-gate-amount tnum">{p ? A.amount(p.bytes, p.bytesTotal, p.files, p.filesTotal) : '\u00a0'}</p>
        </div>
      </div>
    );
  }
  const head = view.reason === 'storage' ? A.stoppedStorage : view.reason === 'offline' ? A.stoppedOffline : A.stoppedNetwork;
  const detail = view.reason === 'storage' && view.need !== undefined && view.free !== undefined ? A.room(view.need, view.free) : A.missing(view.missing);
  return (
    <div className="update-gate audio-gate" role="alertdialog" aria-modal="true" aria-labelledby="audio-gate-head" aria-describedby="audio-gate-detail">
      <div className="update-gate-inner">
        <p className="update-gate-name">Valyrio udrir</p>
        <div className="update-gate-line" aria-hidden="true">
          <span />
        </div>
        <p className="audio-gate-head" id="audio-gate-head">
          {head}
        </p>
        <p className="update-gate-status tnum" id="audio-gate-detail">
          {detail}
        </p>
        <div className="audio-gate-actions">
          <button type="button" className="btn btn-primary" autoFocus onClick={retry}>
            {A.retry}
          </button>
          <button type="button" className="link-btn" onClick={() => setView({ s: 'hidden' })}>
            {A.skip}
          </button>
        </div>
      </div>
    </div>
  );
}
